/**
 * f14.2: "This decision is final" -- the one confirmation before a variance is
 * accepted or rejected (design D8). The comment is optional (≤ 500 characters,
 * enforced by the server too). The server decides; this only asks.
 */

import { useState } from 'react';
import { Check, Lock, X } from 'lucide-react';

import type { ReconciliationVariance } from '@/api/client';
import { DialogField, Modal } from '@/routes/reference-data/Modal';

import { formatAmount, monthLabel } from './reconciliationFormat';

export const COMMENT_MAX = 500;

export function DecisionConfirmDialog({
  variance,
  decision,
  busy,
  onConfirm,
  onClose,
}: {
  variance: ReconciliationVariance;
  decision: 'ACCEPT' | 'REJECT';
  busy: boolean;
  onConfirm: (comment: string | null) => void;
  onClose: () => void;
}) {
  const [comment, setComment] = useState('');
  const accept = decision === 'ACCEPT';
  const currency = variance.currency_code;
  const trimmed = comment.trim();

  return (
    <Modal
      title={accept ? 'Accept this variance?' : 'Reject this variance?'}
      subtitle={`${variance.client.name} · ${variance.element_reference} · ${monthLabel(variance.period_month)}`}
      onClose={onClose}
      onSubmit={() => onConfirm(trimmed ? trimmed : null)}
      busy={busy}
      submitDisabled={comment.length > COMMENT_MAX}
      submitLabel={accept ? 'Accept' : 'Reject'}
      submitIcon={accept ? <Check aria-hidden="true" /> : <X aria-hidden="true" />}
      submitClassName={accept ? 'btn btn-primary' : 'btn btn-danger'}
      layout="plain"
    >
      <div className="alert alert-warning mb-4" role="note">
        <Lock aria-hidden="true" />
        <div>
          <div className="a-title">This decision is final</div>
          <div className="a-body">
            {accept
              ? `The closed month will equal ${formatAmount(variance.incoming_amount, currency)}; the unit is recalculated.`
              : `The closed month keeps ${formatAmount(variance.reference_amount, currency)}. Nothing else changes.`}{' '}
            It cannot be undone; a later ingest raises a new variance instead.
          </div>
        </div>
      </div>
      <DialogField label="Comment (optional)" htmlFor="decision-comment"
        problem={comment.length > COMMENT_MAX ? `At most ${COMMENT_MAX} characters.` : null}
        hint={`${comment.length} / ${COMMENT_MAX}`}>
        <textarea
          id="decision-comment"
          rows={3}
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          placeholder={accept ? 'Why the new figure is right' : 'e.g. Kimble late timesheets'}
        />
      </DialogField>
    </Modal>
  );
}
